import { http } from '@/lib/http';

export type PaymentMethodType = 'cash' | 'check' | 'zelle' | 'venmo' | 'stripe' | 'other';

/** Mirrors `repo.PaymentMethod` in the vizitpro-api swagger spec. */
export interface PaymentMethodDto {
  id: string;
  business_id: string;
  type: PaymentMethodType;
  label: string | null;
  /** Type-specific details, e.g. a Zelle phone number or Venmo handle. */
  config: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Request body for `POST /businesses/{business_id}/payment-methods` —
 * mirrors `controller.createPaymentMethodRequest`. `type` excludes `'stripe'`
 * — that entry is auto-created by the Connect onboarding webhook, never
 * through this endpoint.
 */
export interface CreatePaymentMethodRequest {
  type: Exclude<PaymentMethodType, 'stripe'>;
  label?: string;
  config?: Record<string, unknown>;
}

/**
 * Request body for `PATCH /businesses/{business_id}/payment-methods/{id}` —
 * mirrors `controller.updatePaymentMethodRequest`. All fields optional; the
 * `'stripe'` method can't be edited here.
 */
export interface UpdatePaymentMethodRequest {
  label?: string;
  config?: Record<string, unknown>;
  is_active?: boolean;
}

export function paymentMethodsQueryKey(businessId: string) {
  return ['payment-methods', businessId] as const;
}

/**
 * Calls `GET /businesses/{business_id}/payment-methods`. Returns every
 * payment method configured on the business (active and inactive).
 * Caller must be the business owner.
 */
export async function listPaymentMethods(businessId: string): Promise<PaymentMethodDto[]> {
  const { data } = await http.get<PaymentMethodDto[]>(
    `/businesses/${businessId}/payment-methods`,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/payment-methods`. Adds a Zelle,
 * Venmo, cash, or check entry a business can offer on its invoices.
 * Caller must be the business owner.
 */
export async function createPaymentMethod(
  businessId: string,
  body: CreatePaymentMethodRequest,
): Promise<PaymentMethodDto> {
  const { data } = await http.post<PaymentMethodDto>(
    `/businesses/${businessId}/payment-methods`,
    body,
  );
  return data;
}

/** Calls `PATCH /businesses/{business_id}/payment-methods/{id}`. */
export async function updatePaymentMethod(
  businessId: string,
  id: string,
  body: UpdatePaymentMethodRequest,
): Promise<PaymentMethodDto> {
  const { data } = await http.patch<PaymentMethodDto>(
    `/businesses/${businessId}/payment-methods/${id}`,
    body,
  );
  return data;
}
